// useForm.ts - полный файл с изменениями
import React, { SetStateAction, useCallback, useEffect, useState } from 'react'
import {
  CategoryInterface,
  OptionItemInterface,
  PromoInterface,
  SaleItemInterface,
  StoreInterface,
  ValidationResultInterface,
  ValidationRulesInterface,
} from '../../../../../../common/types/interfaces'
import { getSalesApi } from '../../../../../../salesApi/salesApi'
import { useFiles } from '../../../../../../common/hooks/useFiles'
import { useValidation } from '../../../../../../common/hooks/useValidation'
import { v4 } from 'uuid'
import {
  promoValidationsExtended,
  minUploadFilesCount,
} from '../../../../../../common/utils/constants'
import { useInput } from '../../../../../../common/hooks/useInput'
import { processAttributes } from '../../../../../../common/utils/functions/processAttributes'
import { useSelector } from 'react-redux'
import { selectCurrentCluster } from '../../../../../../store/slices/currentClusterSlice'
import { useCommonLoader } from '../../../../../../common/hooks/useCommonLoader'
import { delayAction } from '../../../../../../common/utils/functions/delayAction'

const validations: ValidationRulesInterface[] = promoValidationsExtended

export const useForm = (
  data: PromoInterface,
  setData: React.Dispatch<SetStateAction<PromoInterface>>,
  edit: boolean,
  getList: () => void,
  setModalActive: React.Dispatch<SetStateAction<boolean>>,
  el?: PromoInterface,
) => {
  const [salesApi] = useState(getSalesApi())
  const [validationResult, setValidationResult] = useState<ValidationResultInterface[]>([])
  const [disabled, setDisabled] = useState<boolean>(true)
  const [categoryList, setCategoryList] = useState<CategoryInterface[]>([])
  const [storeList, setStoreList] = useState<StoreInterface[]>([])
  const [saleItemList, setSaleItemList] = useState<SaleItemInterface[]>([])
  const { files, setFiles, pairs, setPairs, dataInit, fetchFiles } = useFiles()
  const { validate, checkAndAddValidation, setDirty } = useValidation(
    setValidationResult,
    validationResult,
    validations,
  )
  const { inputHandler } = useInput(setData)
  const currentCluster = useSelector(selectCurrentCluster)
  const { showCommonLoader, hideCommonLoader } = useCommonLoader()

  useEffect(() => {
    if (el?.files?.length) {
      fetchFiles(el.files)
      el.files = []
    }
    if (el) setData(el)
  }, [el])

  const getValidationResult = (fieldName: string) => {
    return validationResult.find((el) => el.name === fieldName)
  }

  const getCategoryList = useCallback(async () => {
    try {
      const result = await salesApi.getCategoryList()
      if (result) setCategoryList(result?.data)
    } catch (error) {}
  }, [salesApi])

  const getStoreList = useCallback(async () => {
    try {
      const result = await salesApi.getStoreList()
      if (result) setStoreList(result?.data)
    } catch (error) {}
  }, [salesApi])

  const getSaleItemList = useCallback(async () => {
    try {
      const result = await salesApi.getSaleItemList()
      if (result) setSaleItemList(result?.data)
    } catch (error) {}
  }, [salesApi])

  const formAction = useCallback(async () => {
    try {
      await showCommonLoader()
      await delayAction(1000)
      console.log('📤 Отправляемые файлы:', files.map(f => ({ name: f.name, type: f.type })));
      if (data.id) {
        const { createdAt, updatedAt, mailingSummary, ...rest } = data
        await salesApi.updatePromo({ ...rest, file: dataInit(files) })
      } else if (currentCluster) {
        await salesApi.createPromo({ ...data, clusterId: currentCluster.id, file: dataInit(files) })
      }
      await getList()
      await hideCommonLoader()
      setModalActive(false)
    } catch (error) {
      await hideCommonLoader()
    }
  }, [
    salesApi,
    data,
    files,
    dataInit,
    getList,
    setModalActive,
    currentCluster,
    showCommonLoader,
    hideCommonLoader,
  ])

  const createSelectList = useCallback(
    <T extends { id?: number | string | undefined; name: string }>(
      list: T[],
    ): OptionItemInterface[] => {
      return list.map((item) => ({
        id: String(item.id),
        label: `${item.name}`,
        name: item.name,
        value: String(item.id),
      }))
    },
    [],
  )

  const createCategorySelectList = useCallback(() => {
    return createSelectList(categoryList)
  }, [categoryList, createSelectList])

  const createSaleItemSelectList = useCallback(() => {
    return saleItemList.map((item) => ({
      id: String(item.id),
      label: '#' + item.id + ' ' + item.name + processAttributes(item.attributes),
      name: item.name,
      value: String(item.id),
    }))
  }, [saleItemList])

  const createStoreSelectList = useCallback(() => {
    return createSelectList(storeList)
  }, [storeList, createSelectList])

  const toggleAction = useCallback(
    (fieldName: string) => {
      setData((prev) => {
        const booleanFields = [
          'useItems',
          'useCategories',
          'useStores',
          'useDiscountCode',
          'datesImportant',
          'isActive',
          'isVisible',
          'showText',
          'forFirstPurchase',
          'forPickup',
          'giftBySum',
        ]
        if (!booleanFields.includes(fieldName)) return prev

        const newValue = !prev[fieldName as keyof PromoInterface] as boolean
        const updates: Partial<PromoInterface> = { [fieldName]: newValue }

        // Очистка при выключении
        if (fieldName === 'useDiscountCode' && newValue === false) {
          updates.discountCode = ''
        }

        if (fieldName === 'useStores' && newValue === false) {
          updates.storesFor = []
        }

        if (fieldName === 'datesImportant' && newValue === false) {
          updates.startDate = new Date()
          updates.endDate = new Date()
        }

        // Взаимоисключения при включении forPickup
        if (fieldName === 'forPickup' && newValue === true) {
          updates.useItems = false
          updates.useCategories = false
          updates.useDiscountCode = false
          updates.giftBySum = false
          updates.itemsFor = []
          updates.categoriesFor = []
          updates.discountCode = ''
          updates.minSum = undefined
          updates.giftItemIds = []
        }

        // Взаимоисключения при включении giftBySum
        if (fieldName === 'giftBySum' && newValue === true) {
          updates.useItems = false
          updates.useCategories = false
          updates.useDiscountCode = false
          updates.forPickup = false
          updates.itemsFor = []
          updates.categoriesFor = []
          updates.discountCode = ''
          updates.storesFor = []
        }

        // При включении useItems/useCategories/useDiscountCode выключаем forPickup и giftBySum
        if (
          (fieldName === 'useItems' ||
            fieldName === 'useCategories' ||
            fieldName === 'useDiscountCode') &&
          newValue === true
        ) {
          updates.forPickup = false
          updates.giftBySum = false
          updates.minSum = undefined
          updates.giftItemIds = []
        }

        // Старая логика взаимовыключения useItems/useCategories/useDiscountCode
        if (fieldName === 'useItems' && newValue === true) {
          updates.useCategories = false
          updates.useDiscountCode = false
          updates.categoriesFor = []
          updates.discountCode = ''
        }

        if (fieldName === 'useCategories' && newValue === true) {
          updates.useItems = false
          updates.useDiscountCode = false
          updates.itemsFor = []
          updates.discountCode = ''
        }

        if (fieldName === 'useDiscountCode' && newValue === true) {
          updates.useItems = false
          updates.useCategories = false
          updates.itemsFor = []
          updates.categoriesFor = []
        }

        return { ...prev, ...updates }
      })
    },
    [setData],
  )

  const multipleSearchSelectHandler = useCallback(
    (value: string | number | boolean, fieldName: string, type: string = '') => {
      if (!value) return
      const numValue = Number(value)
      if (isNaN(numValue)) return

      setData((prev) => {
        if (fieldName === 'itemsFor') {
          if (prev.itemsFor.includes(numValue)) return prev
          const newArray = [...prev.itemsFor, numValue]
          inputHandler(newArray, fieldName, 'array')
          return { ...prev, itemsFor: newArray }
        }

        if (fieldName === 'categoriesFor') {
          if (prev.categoriesFor.includes(numValue)) return prev
          const newArray = [...prev.categoriesFor, numValue]
          inputHandler(newArray, fieldName, 'array')
          return { ...prev, categoriesFor: newArray }
        }

        if (fieldName === 'storesFor') {
          if (prev.storesFor.includes(numValue)) return prev
          const newArray = [...prev.storesFor, numValue]
          inputHandler(newArray, fieldName, 'array')
          return { ...prev, storesFor: newArray }
        }

        if (fieldName === 'giftItemIds') {
          if (prev.giftItemIds?.includes(numValue)) return prev
          const newArray = [...(prev.giftItemIds || []), numValue]
          inputHandler(newArray, fieldName, 'array')
          return { ...prev, giftItemIds: newArray }
        }

        return prev
      })
    },
    [setData, inputHandler],
  )

  const multipleSearchSelectRemoveHandler = useCallback(
    (value: string | number | boolean, fieldName: string) => {
      if (!value) return
      const numValue = Number(value)
      if (isNaN(numValue)) return

      setData((prev) => {
        if (fieldName === 'itemsFor') {
          return { ...prev, itemsFor: prev.itemsFor.filter((el) => el !== numValue) }
        }
        if (fieldName === 'categoriesFor') {
          return { ...prev, categoriesFor: prev.categoriesFor.filter((el) => el !== numValue) }
        }
        if (fieldName === 'storesFor') {
          return { ...prev, storesFor: prev.storesFor.filter((el) => el !== numValue) }
        }
        if (fieldName === 'giftItemIds') {
          return { ...prev, giftItemIds: prev.giftItemIds?.filter((el) => el !== numValue) || [] }
        }
        return prev
      })
    },
    [setData],
  )

  useEffect(() => {
    getCategoryList()
  }, [getCategoryList])
  useEffect(() => {
    getSaleItemList()
  }, [getSaleItemList])
  useEffect(() => {
    getStoreList()
  }, [getStoreList])

  useEffect(() => {
    const validationFields = validations.map((v) => v.fieldName)

    validationFields.forEach((field) => {
      const hasValidation = validationResult.some((v) => v.name === field)
      if (!hasValidation) {
        checkAndAddValidation(field)
      }
    })

    validationFields.forEach((field) => {
      const value = data[field as keyof PromoInterface]
      validate(field, value)
    })
  }, [
    data.name,
    data.description,
    data.discount,
    data.discountCode,
    data.itemsFor,
    data.categoriesFor,
    data.storesFor,
    data.startDate,
    data.endDate,
    data.minSum,
    data.giftItemIds,
    validationResult.length,
  ])

  useEffect(() => {
    const hasMinFiles = files.length >= minUploadFilesCount

    const allErrors: string[] = []

    validationResult
      .filter((el) => el.notValid)
      .forEach((el) => {
        if (el.name === 'itemsFor' || el.name === 'categoriesFor' || el.name === 'storesFor') return
        if (el.name === 'discountCode' && !data.useDiscountCode) return
        if (el.name === 'startDate' || el.name === 'endDate') return
        if (el.name === 'minSum' && !data.giftBySum) return
        if (el.name === 'giftItemIds' && !data.giftBySum) return
        allErrors.push(el.name)
      })

    if (data.useItems && data.itemsFor.length === 0) {
      allErrors.push('itemsFor')
    }

    if (data.useCategories && data.categoriesFor.length === 0) {
      allErrors.push('categoriesFor')
    }

    if (data.useStores && data.storesFor.length === 0) {
      allErrors.push('storesFor')
    }

    if (data.useDiscountCode && data.discountCode.trim() === '') {
      allErrors.push('discountCode')
    }

    // minSum обязателен только для giftBySum
    if (data.giftBySum) {
      if (!data.minSum || Number(data.minSum) <= 0) {
        allErrors.push('minSum')
      }
      if (!data.giftItemIds || data.giftItemIds.length === 0) {
        allErrors.push('giftItemIds')
      }
    }

    const hasErrors = allErrors.length > 0

    let discountValue = Number(data.discount)
    let isDiscountValid = true

    if (data.forPickup) {
      isDiscountValid = !isNaN(discountValue) && discountValue > 0 && discountValue <= 100
    } else {
      isDiscountValid = !isNaN(discountValue) && discountValue <= 100
    }

    const shouldBeDisabled = !hasMinFiles || hasErrors || !isDiscountValid

    setDisabled(shouldBeDisabled)
  }, [
    files.length,
    validationResult,
    data.useDiscountCode,
    data.discountCode,
    data.useItems,
    data.itemsFor,
    data.useCategories,
    data.categoriesFor,
    data.useStores,
    data.storesFor,
    data.discount,
    data.forPickup,
    data.giftBySum,
    data.minSum,
    data.giftItemIds,
  ])

  useEffect(() => {
    if (data.forFirstPurchase && (data.useItems || data.useCategories)) {
      if (data.useStores) {
        setData((prev) => ({ ...prev, useStores: false, storesFor: [] }))
      }
    }
  }, [data.forFirstPurchase, data.useItems, data.useCategories, data.useStores, setData])

  return {
    formAction,
    data,
    inputHandler,
    setDirty,
    getValidationResult,
    files,
    pairs,
    setFiles,
    setPairs,
    createCategorySelectList,
    disabled,
    toggleAction,
    multipleSearchSelectHandler,
    multipleSearchSelectRemoveHandler,
    createSaleItemSelectList,
    createStoreSelectList,
  }
}
